import { z } from 'zod';
import { FacilityDtoSchema } from './facilities-api.dto';

export * from './facilities-api.dto';

/**
 * Asset lifecycle enumeration
 */
export enum AssetLifecycleFunction {
  Assembly = 'Assembly',
  CheckIn = 'CheckIn',
  Surgery = 'Surgery',
  Recovery = 'Recovery',
  Picking = 'Picking',
  NO_OP = 'NO_OP',
  Storage = 'Storage',
}
export const AssetLifecycleFunctionSchema = z.nativeEnum(
  AssetLifecycleFunction
);

export enum LocationTypeName {
  UNKNOWN = 'Unknown',
  OPERATING_ROOM = 'Operating Room',
  CHECKPOINT = 'Checkpoint',
  HUB = 'Hub',
  CIRCULATION_SPACE = 'Circulation Space',
  RACK = 'Rack',
  STORAGE_ROOM = 'Storage Room',
  STERILE_PROCESSING = 'Sterile Processing',
  PATIENT_PREOP = 'Patient Preop',
  PATIENT_RECOVERY = 'Patient Recovery',
}

export const LocationTypeDtoSchema = z.object({
  /** Unique identifier of the location type */
  id: z.string(),
  /** Human-readable location type name */
  name: z.nativeEnum(LocationTypeName),
});

export type LocationTypeDto = z.infer<typeof LocationTypeDtoSchema>;

export const LocationBriefDtoSchema = z.object({
  /** Unique identifier of the location */
  id: z.string(),
  /** Name of the location */
  name: z.string(),
  /** Department or unit of the location */
  departmentOrUnit: z.string(),
  /** Floor identifier of the location */
  floor: z.string(),
  /** Type/category of location */
  locationType: LocationTypeDtoSchema.nullable().optional(),
});

export type LocationBriefDto = z.infer<typeof LocationBriefDtoSchema>;

/**
 * Paginated response from the assets API
 */
export const LocationResponseDtoSchema = z.object({
  /** Unique identifier of the location */
  id: z.string(),
  /** Name of the location */
  name: z.string(),
  /** Department or unit */
  departmentOrUnit: z.string(),
  /** Floor identifier */
  floor: z.string(),
  /** Facility details */
  facility: FacilityDtoSchema,
  /** Type/category of location */
  locationType: LocationTypeDtoSchema.nullable().optional(),
  /** Additional details */
  additionalDetails: z.string().nullable().optional(),
  /** Whether this is a drop-off point */
  dropoff: z.boolean().nullable().optional(),
  /** Proximity range (stringified decimal) */
  proximityRange: z.string().nullable().optional(),
  /** X coordinate */
  xCoordinate: z.number().nullable().optional(),
  /** Y coordinate */
  yCoordinate: z.number().nullable().optional(),
  /** Z coordinate */
  zCoordinate: z.number().nullable().optional(),
  /** Asset lifecycle function of the location */
  assetLifecycleFunction: AssetLifecycleFunctionSchema.nullable(),
  /** Parent location (if this is a child location) */
  parent: LocationBriefDtoSchema.nullable(),
  /** Child locations (if this location has children) */
  children: z.array(LocationBriefDtoSchema).optional(),
  /** Creation timestamp */
  createdAt: z.string(),
  /** Last update timestamp */
  updatedAt: z.string(),
});
export type LocationResponseDto = z.infer<typeof LocationResponseDtoSchema>;

export const LocationsResponseDtoSchema = z.array(LocationResponseDtoSchema);

export const CreateLocationDtoSchema = z.object({
  name: z.string().max(255),
  departmentOrUnit: z.string().max(255),
  floor: z.string().max(128),
  facilityId: z.string().uuid(),
  locationTypeId: z.string().uuid().optional(),
  parentId: z.string().uuid().optional(),
  additionalDetails: z.string().optional(),
  dropoff: z.boolean().optional(),
  proximityRange: z.string().optional(),
  xCoordinate: z.number().optional(),
  yCoordinate: z.number().optional(),
  zCoordinate: z.number().optional(),
  assetLifecycleFunction: AssetLifecycleFunctionSchema.optional(),
});
export type CreateLocationDto = z.infer<typeof CreateLocationDtoSchema>;

export const UpdateLocationDtoSchema = CreateLocationDtoSchema.partial();
export type UpdateLocationDto = z.infer<typeof UpdateLocationDtoSchema>;

export const LocationTypeResponseDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type LocationTypeResponseDto = z.infer<typeof LocationTypeResponseDtoSchema>;

export const LocationTypesResponseDtoSchema = z.array(LocationTypeResponseDtoSchema);

export const FacilityResponseDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  timeZone: z.string(),
  contactInfo: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FacilityResponseDto = z.infer<typeof FacilityResponseDtoSchema>;

export const PaginatedFacilityResponseDtoSchema = z.object({
  data: z.array(FacilityResponseDtoSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});
export type PaginatedFacilityResponseDto = z.infer<typeof PaginatedFacilityResponseDtoSchema>;

export const PaginatedLocationResponseDtoSchema = z.object({
  data: z.array(LocationResponseDtoSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});
export type PaginatedLocationResponseDto = z.infer<typeof PaginatedLocationResponseDtoSchema>;
